/*
 * Motureo: warstwa ruchu (etap 1b). Wymaga globalnych `gsap` i `ScrollTrigger` (GSAP 3.15.0, licencja standardowa
 * Webflow, docs/motyw/ruch/README.md), wczytanych z atrybutem defer przed tym plikiem przez snippets/motureo-motion.liquid.
 *
 * Atrybuty w sekcjach (żadnych tekstów):
 *   data-motion="reveal"   element wchodzi raz (opacity 0 → 1, y 24 px → 0), gdy pojawi się w widoku
 *   data-motion="stagger"  dzieci kontenera wchodzą jedno po drugim (odstęp 60 ms)
 *   data-motion="counter"  liczba odlicza od 0 do data-motion-to (albo do liczby z tekstu); tekst końcowy zostaje
 *                          dokładnie taki, jaki wyrenderował serwer; w elemencie powinna być sama liczba
 *   data-motion="scrub"    element przesuwa się o data-motion-x / data-motion-y (px) w rytm przewijania;
 *                          zakres przewijania wyznacza najbliższy przodek [data-motion-scope] albo sam element
 *
 * Zasady: klasę html.motion ustawia skrypt w <head> tylko przy prefers-reduced-motion: no-preference; motureo.css
 * ukrywa wtedy elementy reveal/stagger, dopóki ten plik nie doda html.motion-ready (bezpiecznik CSS pokazuje je po 2 s).
 * Gdy biblioteka nie dotrze, użytkownik woli mniej ruchu, urządzenie oszczędza dane albo strona jest w edytorze
 * motywu, nic nie jest ukryte i nic się nie rusza. Elementy wstawione później (ponowne renderowanie sekcji Horizona)
 * pozostają widoczne bez animacji.
 *
 * Przewijanie (poprawka 19.10): od 990 px Horizon przewija stronę w div.page-wrapper (base.css), a nie w oknie.
 * Wyzwalacze nasłuchują więc na tym kontenerze, gdy to on się przewija; poniżej 990 px na oknie. Bez tego kafle
 * „Popularne kategorie” zostawały na komputerze z przezroczystością 0, bo okno nigdy się nie przewijało.
 */
(function () {
  'use strict';

  var root = document.documentElement;
  var gsap = window.gsap;
  var ScrollTrigger = window.ScrollTrigger;
  var connection = navigator.connection;
  var saveData = Boolean(connection && connection.saveData);
  var lowMemory = typeof navigator.deviceMemory === 'number' && navigator.deviceMemory <= 2;
  var designMode = Boolean(window.Shopify && window.Shopify.designMode);
  // Gdy skrypty dotarły po bezpieczniku CSS (2 s), elementy są już widoczne; nie chowamy ich ponownie.
  var late = window.performance && performance.now() > 1800;

  function showEverything() {
    root.classList.remove('motion');
    root.classList.add('motion-ready');
  }

  if (!gsap || !ScrollTrigger || saveData || lowMemory || designMode) {
    showEverything();
    return;
  }

  gsap.registerPlugin(ScrollTrigger);
  gsap.defaults({ ease: 'power2.out', duration: 0.45 });
  ScrollTrigger.config({ ignoreMobileResize: true });

  var DESKTOP = '(min-width: 990px)';
  var REVEAL_FROM = { opacity: 0, y: 24 };
  var REVEAL_TO = { opacity: 1, y: 0, stagger: 0.06, overwrite: 'auto' };
  // Bez clamp(): przesuwa start elementów z pierwszego ekranu na 0, a przy przewinięciu 0 wyzwalacz nie rusza
  // (sprawdzone 19.10 - licznik stał). Elementów data-motion nie stawiać w ostatnich 12% wysokości strony.
  var START = 'top 88%';

  function toArray(list) {
    return Array.prototype.slice.call(list);
  }

  // Kontener, który faktycznie się przewija: div.page-wrapper od 990 px (Horizon), w pozostałych przypadkach okno.
  function findScroller(desktop) {
    var wrapper = document.querySelector('.page-wrapper');
    // overflow hidden (otwarta szuflada, html[scroll-lock]) to nadal ten sam kontener przewijania.
    if (desktop && wrapper && getComputedStyle(wrapper).overflowY !== 'visible') return wrapper;
    return window;
  }

  // Element, który już wszedł, nie chowa się ponownie, gdy zmiana progu 990 px uruchamia kontekst od nowa.
  function markDone(el) {
    el.setAttribute('data-motion-done', '');
  }

  function notDone(el) {
    return !el.hasAttribute('data-motion-done');
  }

  function attr(el, name, fallback) {
    var value = parseFloat(el.getAttribute('data-motion-' + name));
    return isFinite(value) ? value : fallback;
  }

  function reveal(targets) {
    if (!targets.length) return;
    gsap.set(targets, REVEAL_FROM);
    ScrollTrigger.batch(targets, {
      start: START,
      once: true,
      onEnter: function (batch) {
        batch.forEach(markDone);
        gsap.to(batch, REVEAL_TO);
      },
    });
  }

  function stagger(container) {
    var items = toArray(container.children);
    if (!items.length) return;
    gsap.set(items, REVEAL_FROM);
    ScrollTrigger.create({
      trigger: container,
      start: START,
      once: true,
      onEnter: function () {
        markDone(container);
        gsap.to(items, REVEAL_TO);
      },
    });
  }

  function counter(el) {
    var finalText = el.textContent;
    var parsed = parseFloat(finalText.replace(/\s/g, '').replace(',', '.').replace(/[^\d.-]/g, ''));
    var to = attr(el, 'to', parsed);
    if (!isFinite(to)) return;
    var format = new Intl.NumberFormat(root.lang || undefined, { maximumFractionDigits: 0 });
    var state = { value: 0 };
    ScrollTrigger.create({
      trigger: el,
      start: 'top 90%',
      once: true,
      onEnter: function () {
        // Szerokość z tekstu końcowego, żeby rosnąca liczba nie przesuwała sąsiadów.
        el.style.minWidth = el.offsetWidth + 'px';
        el.style.display = 'inline-block';
        gsap.to(state, {
          value: to,
          duration: 1.2,
          ease: 'power1.out',
          onUpdate: function () {
            el.textContent = format.format(state.value);
          },
          onComplete: function () {
            el.textContent = finalText;
          },
        });
      },
    });
  }

  function scrub(el) {
    var x = attr(el, 'x', 0);
    var y = attr(el, 'y', 0);
    if (!x && !y) return;
    var scope = el.closest('[data-motion-scope]') || el;
    gsap.fromTo(
      el,
      { x: 0, y: 0 },
      {
        x: x,
        y: y,
        ease: 'none',
        scrollTrigger: { trigger: scope, start: 'top bottom', end: 'bottom top', scrub: true },
      }
    );
  }

  function init(scope) {
    if (!late) {
      reveal(toArray(scope.querySelectorAll('[data-motion="reveal"]')).filter(notDone));
      toArray(scope.querySelectorAll('[data-motion="stagger"]')).filter(notDone).forEach(stagger);
    }
    toArray(scope.querySelectorAll('[data-motion="counter"]')).forEach(counter);
    toArray(scope.querySelectorAll('[data-motion="scrub"]')).forEach(scrub);
  }

  var media = gsap.matchMedia();

  // all: funkcja uruchamia się przy każdym zestawie warunków (także „mniej ruchu” poniżej 990 px).
  media.add({ all: 'all', motion: '(prefers-reduced-motion: no-preference)', desktop: DESKTOP }, function (context) {
    if (!context.conditions.motion) {
      showEverything();
      return;
    }
    // Zmiana progu 990 px cofa ten kontekst i uruchamia go ponownie z właściwym kontenerem przewijania.
    ScrollTrigger.defaults({ scroller: findScroller(context.conditions.desktop) });
    if (late) {
      root.classList.remove('motion');
    } else {
      root.classList.add('motion');
    }
    init(document);
    root.classList.add('motion-ready');
    return function () {
      // gsap.matchMedia cofa animacje i style inline z tego kontekstu; zostaje tylko zdjęcie klasy.
      root.classList.remove('motion');
    };
  });
})();
